import React, { useState, useEffect, useCallback, useRef } from 'react'

/* ── helpers ── */
const TEAM_COLORS = {
  customer_support: { bg: '#16A34A', text: '#fff', label: 'Customer Support' },
  ert:              { bg: '#DC2626', text: '#fff', label: 'ERT' },
  station:          { bg: '#EA580C', text: '#fff', label: 'Station Team' },
  aar:              { bg: '#7C3AED', text: '#fff', label: 'AAR' },
  dco:              { bg: '#0284C7', text: '#fff', label: 'DCO' },
  after_sales:      { bg: '#D97706', text: '#fff', label: 'After Sales' },
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

function teamMeta(teamKey) {
  if (!teamKey) return null
  const k = (teamKey || '').toLowerCase().replace(/[\s-]+/g, '_')
  return TEAM_COLORS[k] || { bg: '#6B7280', text: '#fff', label: teamKey }
}

function avatarColor(name) {
  const colors = ['#16A34A','#0284C7','#7C3AED','#DC2626','#EA580C','#D97706','#0F766E','#BE185D']
  let h = 0
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff
  return colors[Math.abs(h) % colors.length]
}

function initials(name) {
  return (name || '??')
    .split(' ').filter(Boolean).slice(0, 2)
    .map(p => p[0]).join('').toUpperCase()
}

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = [1]
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
  if (current < total - 2) pages.push('...')
  pages.push(total)
  return pages
}

/* ── blank form state ── */
const BLANK_FORM = {
  full_name:        '',
  mobile:           '',
  email:            '',
  team:             '',
  agent_id:         '',
  password:         '',
  confirm_password: '',
  new_password:     '',
  feature_access:   [],
}

/* ── sub-components ── */
function Avatar({ name, size = 38 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: avatarColor(name),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.36,
      flexShrink: 0, userSelect: 'none',
    }}>
      {initials(name)}
    </div>
  )
}

function TeamBadge({ team, teamName }) {
  const meta = teamMeta(team)
  if (!meta) return <span style={{ color: '#9CA3AF' }}>—</span>
  return (
    <span style={{
      background: meta.bg, color: meta.text,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {teamName || meta.label}
    </span>
  )
}

function ConfirmModal({ message, onConfirm, onCancel, loading }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28,
        width: 360, boxShadow: '0 20px 60px rgba(0,0,0,.2)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 8 }}>Confirm Delete</div>
        <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, marginBottom: 24 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={loading} style={btnOutline}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ ...btnBase, background: '#DC2626', color: '#fff', border: 'none' }}>
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}

/* ── shared button styles ── */
const btnBase = {
  padding: '8px 18px', borderRadius: 8, fontSize: 13,
  fontWeight: 600, cursor: 'pointer', border: 'none',
}
const btnOutline = {
  ...btnBase, background: '#fff',
  border: '1px solid #D1D5DB', color: '#374151',
}

/* ── main page ── */
export default function UserManagementPage({ fetchJson }) {
  const [users, setUsers]           = useState([])
  const [teams, setTeams]           = useState([])
  const [features, setFeatures]     = useState([])
  const [loading, setLoading]       = useState(false)
  const [search, setSearch]         = useState('')
  const [teamFilter, setTeamFilter] = useState('')
  const [error, setError]           = useState('')

  /* pagination */
  const [page, setPage]           = useState(1)
  const [pageSize, setPageSize]   = useState(10)
  const [totalCount, setTotalCount] = useState(0)

  /* modal state */
  const [modal, setModal]           = useState(null)
  const [editUser, setEditUser]     = useState(null)
  const [editOriginal, setEditOriginal] = useState(null) // snapshot for diff
  const [form, setForm]             = useState(BLANK_FORM)
  const [saving, setSaving]         = useState(false)
  const [formError, setFormError]   = useState('')

  /* delete confirm */
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting]         = useState(false)

  /* password visibility */
  const [showPassword,        setShowPassword]        = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showNewPassword,     setShowNewPassword]     = useState(false)

  const formBodyRef = useRef(null)

  /* reset to page 1 when filters change */
  useEffect(() => { setPage(1) }, [search, teamFilter, pageSize])

  /* auto-scroll form to top when an error appears */
  useEffect(() => {
    if (formError && formBodyRef.current) {
      formBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [formError])

  /* ── fetch users ── */
  const fetchUsers = useCallback(async () => {
    if (!fetchJson) return
    if (search.length === 1) return
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (search.length >= 2) params.set('q', search)
      if (teamFilter)         params.set('team', teamFilter)
      params.set('page', String(page))
      params.set('page_size', String(pageSize))
      const data = await fetchJson(`/api/agents/agent-management/?${params.toString()}`)
      setUsers(Array.isArray(data) ? data : (data?.results || []))
      setTotalCount(data?.count ?? 0)
    } catch (e) {
      setError(e.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [fetchJson, search, teamFilter, page, pageSize])

  /* ── fetch teams ── */
  const fetchTeams = useCallback(async () => {
    if (!fetchJson) return
    try {
      const data = await fetchJson('/api/agents/teams')
      setTeams(Array.isArray(data) ? data : (data?.teams || []))
    } catch { /* best-effort */ }
  }, [fetchJson])

  /* ── fetch features ── */
  const fetchFeatures = useCallback(async () => {
    if (!fetchJson) return
    try {
      const data = await fetchJson('/api/agents/available-features')
      setFeatures(Array.isArray(data) ? data : (data?.features || []))
    } catch { /* best-effort */ }
  }, [fetchJson])

  useEffect(() => { fetchUsers()    }, [fetchUsers])
  useEffect(() => { fetchTeams()    }, [fetchTeams])
  useEffect(() => { fetchFeatures() }, [fetchFeatures])

  /* ── form helpers ── */
  const openAdd = () => {
    setForm(BLANK_FORM)
    setFormError('')
    setEditUser(null)
    setModal('add')
  }

  const openEdit = (user) => {
    const original = {
      full_name:      user.name       || '',
      mobile:         user.mobile     || '',
      email:          user.email      || '',
      team:           user.team?.slug || '',
      agent_id:       user.agent_id   || '',
      feature_access: Array.isArray(user.access) ? user.access.map(a => a.value) : [],
    }
    setForm({ ...original, password: '', confirm_password: '', new_password: '' })
    setEditOriginal(original)
    setFormError('')
    setEditUser(user)
    setModal('edit')
  }

  const closeModal = () => {
    setModal(null); setEditUser(null); setEditOriginal(null); setFormError('')
    setShowPassword(false); setShowConfirmPassword(false); setShowNewPassword(false)
  }

  const handleField = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handlePhone = (raw) => {
    if (!raw.startsWith('+91')) {
      handleField('mobile', '+91')
      return
    }
    handleField('mobile', raw)
  }

  const toggleFeature = (value) => {
    setForm(f => ({
      ...f,
      feature_access: f.feature_access.includes(value)
        ? f.feature_access.filter(v => v !== value)
        : [...f.feature_access, value],
    }))
  }

  const handleSave = async () => {
    if (!form.full_name.trim()) { setFormError('Name is required'); return }
    if (!form.email.trim())     { setFormError('Email is required'); return }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setFormError('Enter a valid email address'); return }

    // phone validation — must be +91 followed by exactly 10 digits
    if (!form.mobile || form.mobile === '+91') { setFormError('Mobile number is required'); return }
    if (!/^\+91\d{10}$/.test(form.mobile)) {
      setFormError('Phone must be in format +91XXXXXXXXXX (10 digits after +91)')
      return
    }

    if (!form.agent_id.trim()) { setFormError('Agent ID is required'); return }

    if (modal === 'add') {
      if (!form.password)                          { setFormError('Password is required'); return }
      if (form.password.length < 6)                { setFormError('Password must be at least 6 characters'); return }
      if (!form.confirm_password)                  { setFormError('Confirm password is required'); return }
      if (form.password !== form.confirm_password) { setFormError('Passwords do not match'); return }
    }

    setSaving(true)
    setFormError('')
    try {
      let body = {}

      if (modal === 'edit' && editOriginal) {
        // only include fields that changed
        if (form.full_name !== editOriginal.full_name)   body.name      = form.full_name
        if (form.mobile    !== editOriginal.mobile)      body.phone     = form.mobile
        if (form.email     !== editOriginal.email)       body.email     = form.email
        if (form.team      !== editOriginal.team)        body.team      = form.team || null
        if (form.agent_id  !== editOriginal.agent_id)   body.agent_id  = form.agent_id

        const origAccess = [...editOriginal.feature_access].sort().join(',')
        const newAccess  = [...form.feature_access].sort().join(',')
        if (newAccess !== origAccess) body.access = form.feature_access

        if (form.new_password.trim()) body.new_password = form.new_password

        if (Object.keys(body).length === 0) { closeModal(); return } // nothing changed

        await fetchJson(`/api/agents/agent-management/${editUser.id}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      } else {
        // add — send all fields
        body = {
          name:     form.full_name,
          phone:    form.mobile,
          email:    form.email,
          agent_id: form.agent_id,
          team:     form.team     || undefined,
          access:   form.feature_access,
          password: form.password,
        }

        await fetchJson('/api/agents/agent-management/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      }

      closeModal()
      fetchUsers()
    } catch (e) {
      setFormError(e.message || 'Something went wrong')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await fetchJson(`/api/agents/agent-management/${deleteTarget.id}/`, { method: 'DELETE' })
      setDeleteTarget(null)
      fetchUsers()
    } catch (e) {
      alert(e.message || 'Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const pageNumbers = buildPageNumbers(page, totalPages)

  /* ── render ── */
  return (
    <div style={{ padding: '4px 0', maxWidth: 1100 }}>

      {/* header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>User Management</div>
        <button
          onClick={openAdd}
          style={{
            ...btnBase,
            background: '#0090D9', color: '#fff',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 14,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add User
        </button>
      </div>

      {/* filters row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 380 }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }}
            width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search name, email, mobile..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 34px',
              border: '1px solid #D1D5DB', borderRadius: 8,
              fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827',
            }}
          />
        </div>

        <div className="t360-select-wrap" style={{ minWidth: 160 }}>
          <select className="t360-select" value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
            <option value="">All Teams</option>
            {teams.length > 0
              ? teams.map(t => <option key={t.id} value={t.slug}>{t.name}</option>)
              : Object.entries(TEAM_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)
            }
          </select>
          <span className="t360-select-arrow">▼</span>
        </div>

        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>
          {loading ? 'Loading…' : `${totalCount} user${totalCount !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* error */}
      {error && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA',
          color: '#DC2626', borderRadius: 8, padding: '10px 14px',
          fontSize: 13, marginBottom: 14,
        }}>
          {error}
        </div>
      )}

      {/* table */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              {['NAME','MOBILE','EMAIL','TEAM','AGENT ID','ACCESS','ACTIONS'].map(col => (
                <th key={col} style={{
                  padding: '11px 16px', textAlign: 'left',
                  fontSize: 11, fontWeight: 700, color: '#6B7280',
                  letterSpacing: '0.05em', whiteSpace: 'nowrap',
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                  Loading users…
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                  No users found
                </td>
              </tr>
            )}
            {!loading && users.map((u, i) => (
              <tr
                key={u.id || i}
                style={{
                  borderBottom: i < users.length - 1 ? '1px solid #F3F4F6' : 'none',
                  transition: 'background .15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={u.name} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.name || '—'}</span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
                  {u.mobile || '—'}
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>
                  {u.email || '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <TeamBadge team={u.team?.slug} teamName={u.team?.name} />
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', fontFamily: 'monospace' }}>
                  {u.agent_id || '—'}
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151', maxWidth: 220 }}>
                  {Array.isArray(u.access) && u.access.length > 0
                    ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {u.access.map(a => (
                          <span key={a.value} style={{
                            background: '#EFF8FF', color: '#0369A1',
                            border: '1px solid #BAE6FD',
                            borderRadius: 4, padding: '2px 7px',
                            fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                          }}>
                            {a.label}
                          </span>
                        ))}
                      </div>
                    )
                    : <span style={{ color: '#9CA3AF' }}>—</span>
                  }
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => openEdit(u)}
                      style={{
                        padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', background: '#fff',
                        border: '1.5px solid #0090D9', color: '#0090D9',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#EFF8FF'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(u)}
                      style={{
                        padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', background: '#fff',
                        border: '1.5px solid #DC2626', color: '#DC2626',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {!loading && totalCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          marginTop: 14, flexWrap: 'wrap',
        }}>
          {/* prev */}
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="t360-page-btn"
            style={{ opacity: page === 1 ? 0.4 : 1 }}
          >
            ‹
          </button>

          {/* page numbers */}
          {pageNumbers.map((p, i) =>
            p === '...'
              ? <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#9CA3AF', fontSize: 13 }}>…</span>
              : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`t360-page-btn${page === p ? ' active' : ''}`}
                >
                  {p}
                </button>
              )
          )}

          {/* next */}
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="t360-page-btn"
            style={{ opacity: page === totalPages ? 0.4 : 1 }}
          >
            ›
          </button>

          {/* rows per page */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Rows per page:</span>
            <div className="t360-select-wrap" style={{ minWidth: 70 }}>
              <select
                className="t360-select"
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="t360-select-arrow">▼</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={e => { if (e.target === e.currentTarget) closeModal() }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, width: 520,
            boxShadow: '0 24px 64px rgba(0,0,0,.22)',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          }}>
            {/* header */}
            <div style={{
              padding: '18px 24px', borderBottom: '1px solid #F3F4F6',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                {modal === 'add' ? 'Add User' : 'Edit User'}
              </div>
              <button onClick={closeModal} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 20, color: '#9CA3AF', lineHeight: 1,
              }}>×</button>
            </div>

            {/* scrollable body */}
            <div ref={formBodyRef} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
              {formError && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA',
                  color: '#DC2626', borderRadius: 8, padding: '8px 12px', fontSize: 13,
                }}>
                  {formError}
                </div>
              )}

              {/* text fields */}
              {[
                { key: 'full_name', label: 'Full Name *',                   type: 'text',  placeholder: 'Enter full name' },
                { key: 'email',     label: 'Email *',                        type: 'email', placeholder: 'email@example.com' },
                { key: 'mobile',    label: 'Mobile * (+91XXXXXXXXXX)',        type: 'text',  placeholder: '+91XXXXXXXXXX' },
                { key: 'agent_id',  label: 'Agent ID * (same as in Ozonetel) / Username', type: 'text',  placeholder: 'Enter Agent ID / Username' },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                    {label}
                  </label>
                  <input
                    type={type}
                    value={form[key]}
                    onChange={e => key === 'mobile' ? handlePhone(e.target.value) : handleField(key, e.target.value)}
                    onFocus={e => {
                      e.target.style.borderColor = '#0090D9'
                      if (key === 'mobile' && !form.mobile) handleField('mobile', '+91')
                    }}
                    placeholder={placeholder}
                    style={{
                      width: '100%', padding: '9px 12px',
                      border: '1px solid #D1D5DB', borderRadius: 8,
                      fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827',
                    }}
                    onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                  />
                  {key === 'email' && form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && (
                    <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                      Enter a valid email address
                    </div>
                  )}
                  {key === 'mobile' && form.mobile && !/^\+91\d{10}$/.test(form.mobile) && (
                    <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>
                      Must be +91 followed by exactly 10 digits
                    </div>
                  )}
                  {key === 'agent_id' && (
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                      This will be used as the username for login.
                    </div>
                  )}
                </div>
              ))}

              {/* team */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                  Team
                </label>
                <div className="t360-select-wrap" style={{ width: '100%' }}>
                  <select
                    className="t360-select"
                    value={form.team}
                    onChange={e => handleField('team', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Select team</option>
                    {teams.length > 0
                      ? teams.map(t => <option key={t.id} value={t.slug}>{t.name}</option>)
                      : Object.entries(TEAM_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)
                    }
                  </select>
                  <span className="t360-select-arrow">▼</span>
                </div>
              </div>

              {/* access — multi-select checkboxes */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                  Access
                </label>
                {features.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 0' }}>Loading features…</div>
                ) : (
                  <div style={{ border: '1px solid #D1D5DB', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                    {features.map((f, idx) => {
                      const checked = form.feature_access.includes(f.value)
                      return (
                        <label
                          key={f.value}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '9px 12px', cursor: 'pointer',
                            borderBottom: idx < features.length - 1 ? '1px solid #F3F4F6' : 'none',
                            background: checked ? '#F0F9FF' : '#fff',
                            transition: 'background .1s',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFeature(f.value)}
                            style={{ accentColor: '#0090D9', width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 13, color: '#111827', userSelect: 'none' }}>{f.label}</span>
                          {checked && (
                            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#0090D9', fontWeight: 600 }}>✓</span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                )}
                {form.feature_access.length > 0 && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 5 }}>
                    {form.feature_access.length} selected
                  </div>
                )}
              </div>

              {/* new password — only for edit */}
              {modal === 'edit' && (
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                    New Password <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(leave blank to keep current)</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={form.new_password}
                      onChange={e => handleField('new_password', e.target.value)}
                      placeholder="Enter new password"
                      style={{
                        width: '100%', padding: '9px 40px 9px 12px',
                        border: '1px solid #D1D5DB', borderRadius: 8,
                        fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827',
                      }}
                      onFocus={e => e.target.style.borderColor = '#0090D9'}
                      onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                    />
                    <button type="button" onClick={() => setShowNewPassword(v => !v)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                      <EyeIcon open={showNewPassword} />
                    </button>
                  </div>
                  {form.new_password.length > 0 && (
                    <div style={{ fontSize: 11, marginTop: 4, color: form.new_password.length >= 6 ? '#16A34A' : '#D97706' }}>
                      {form.new_password.length >= 6
                        ? `✓ ${form.new_password.length} characters`
                        : `${form.new_password.length}/6 — ${6 - form.new_password.length} more needed`}
                    </div>
                  )}
                </div>
              )}

              {/* password — only for add */}
              {modal === 'add' && (
                <>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                      Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={e => handleField('password', e.target.value)}
                        placeholder="Min 6 characters"
                        style={{
                          width: '100%', padding: '9px 40px 9px 12px',
                          border: '1px solid #D1D5DB', borderRadius: 8,
                          fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827',
                        }}
                        onFocus={e => e.target.style.borderColor = '#0090D9'}
                        onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                      />
                      <button type="button" onClick={() => setShowPassword(v => !v)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                        <EyeIcon open={showPassword} />
                      </button>
                    </div>
                    {form.password.length > 0 && (
                      <div style={{ fontSize: 11, marginTop: 4, color: form.password.length >= 6 ? '#16A34A' : '#D97706' }}>
                        {form.password.length >= 6
                          ? `✓ ${form.password.length} characters`
                          : `${form.password.length}/6 — ${6 - form.password.length} more needed`}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                      Confirm Password *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={form.confirm_password}
                        onChange={e => handleField('confirm_password', e.target.value)}
                        placeholder="Re-enter password"
                        style={{
                          width: '100%', padding: '9px 40px 9px 12px',
                          border: `1px solid ${form.confirm_password && form.confirm_password !== form.password ? '#DC2626' : '#D1D5DB'}`,
                          borderRadius: 8,
                          fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#111827',
                        }}
                        onFocus={e => e.target.style.borderColor = '#0090D9'}
                        onBlur={e => e.target.style.borderColor = form.confirm_password && form.confirm_password !== form.password ? '#DC2626' : '#D1D5DB'}
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(v => !v)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', display: 'flex', alignItems: 'center' }}>
                        <EyeIcon open={showConfirmPassword} />
                      </button>
                    </div>
                    {form.confirm_password && form.confirm_password !== form.password && (
                      <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>Passwords do not match</div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* footer */}
            <div style={{
              padding: '16px 24px', borderTop: '1px solid #F3F4F6',
              display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0,
            }}>
              <button onClick={closeModal} disabled={saving} style={btnOutline}>Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ ...btnBase, background: '#0090D9', color: '#fff', border: 'none', minWidth: 110 }}
              >
                {saving ? 'Saving…' : modal === 'add' ? 'Add User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <ConfirmModal
          message={`Delete "${deleteTarget.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  )
}
